import { isApolloError, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import StyledLink from '@ui/atoms/StyledLink';

import Listbox from '@ui/forms/Listbox';
import PhoneTextInput from '@ui/forms/PhoneTextInput';
import TextInput from '@ui/forms/TextInput';

import SubmitButton, { ISubmitButtonProps } from '@ui/molecules/SubmitButton';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { MemberInfo } from '@hooks/useMemberInfo';

import { exists } from '@utils/general';

import relationships from '@data/relationships';

import { IPageProps } from '@folx-types/flow-steps';
import { IEmergencyContact } from '@folx-types/member';

import CREATE_CONTACT from '@graphql/mutations/EmergencyContact/createEmergencyContact.mutation.gql';
import DELETE_CONTACT from '@graphql/mutations/EmergencyContact/deleteEmergencyContact.mutation.gql';
import UPDATE_CONTACT from '@graphql/mutations/EmergencyContact/updateEmergencyContact.mutation.gql';

import theme from '@theme/FolxTheme';

import { schema } from './EmergencyContactsEditStep.data';

export interface IEmergencyContactsPageContent {
	pageHeader: string;
	pageInfo: string;
}

interface IEmergencyContactsEditStepProps extends IPageProps {
	emergencyContact?: IEmergencyContact;
	handleNavigate?: () => void;
	member?: MemberInfo;
	pageContent?: IEmergencyContactsPageContent;
	showCancel?: boolean;
}

const AlignedLink = aligned(StyledLink);
const FullWidthForm = fullWidth<'form'>('form');
const FullWidthSubmitButton = fullWidth<ISubmitButtonProps>(SubmitButton);
const FullWidthButton = fullWidth<IButtonProps>(Button);
const SpacedColumn = spaced<IFlexColumn>(fullWidth<IFlexColumn>(FlexColumn));

const EmergencyContactsEditStep: FC<IEmergencyContactsEditStepProps> = ({
	emergencyContact,
	handleNavigate = () => {
		return;
	},
	member,
	pageContent,
	showCancel = false,
}) => {
	const [submitting, setSubmitting] = useState(false);
	const [phoneValue, setPhoneValue] = useState(emergencyContact?.phoneNumber);

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			firstName: emergencyContact?.firstName,
			lastName: emergencyContact?.lastName,
			relationship: emergencyContact?.relationship,
			phone: emergencyContact?.phoneNumber,
			email: emergencyContact?.email,
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});

	const {
		handleSubmit,
		control,
		watch,
		register,
		setValue,
		formState: { errors },
	} = form;

	const isExistingContact = exists(emergencyContact?.id);

	const [createContact] = useMutation(CREATE_CONTACT);
	const [updateContact] = useMutation(UPDATE_CONTACT);
	const [deleteContact] = useMutation(DELETE_CONTACT);

	const onDelete = async () => {
		const result = await deleteContact({
			variables: { clientMutationId: emergencyContact?.id },
		}).catch((error) => {
			console.log(error);
			return error;
		});

		if (isApolloError(result)) {
			return;
		}

		handleNavigate();
	};

	const onSubmit = async (data: Schema) => {
		setSubmitting(true);

		const contactData = {
			firstName: data?.firstName,
			lastName: data?.lastName,
			relationship: data?.relationship,
			phoneNumber: data?.phone,
			email: data?.email,
			memberUuid: member?.id,
		};

		const updateData = { clientMutationId: emergencyContact?.id ?? '', patch: contactData };
		const createData = {
			input: {
				...contactData,
				createdBy: member?.email,
			},
		};

		const contactMutation = isExistingContact ? updateContact : createContact;
		const mutationData = isExistingContact ? updateData : createData;

		const result = await contactMutation({
			variables: mutationData,
		}).catch((error) => {
			console.log(error);
			return error;
		});

		setSubmitting(false);
		if (isApolloError(result)) {
			return;
		}

		handleNavigate();
	};

	return (
		<FullWidthForm onSubmit={handleSubmit(onSubmit)}>
			<SpacedColumn marginBottom="lgxl" gap="lg">
				<FlexColumn gap="sm">
					<HeadingText>{pageContent?.pageHeader ?? 'Emergency contact'}</HeadingText>
					{exists(pageContent?.pageInfo) && <BasicText>{pageContent?.pageInfo}</BasicText>}
				</FlexColumn>
				<TextInput
					label={'First name'}
					error={!!errors?.firstName}
					errorLabel={errors?.firstName?.message}
					{...register('firstName')}
				/>
				<TextInput
					label={'Last name'}
					error={!!errors?.lastName}
					errorLabel={errors?.lastName?.message}
					{...register('lastName')}
				/>
				<Controller
					control={control}
					name="relationship"
					render={({ field }) => (
						<Listbox
							label={'Relationship'}
							error={!!errors?.relationship}
							errorLabel={errors?.relationship?.message}
							defaultOption="default"
							options={[{ value: 'default', label: 'Select' }, ...relationships]}
							{...field}
						/>
					)}
				/>
				<Controller
					name="phone"
					control={control}
					render={({ field }) => (
						<PhoneTextInput
							placeholder={'(xxx) xxx-xxxx'}
							label={'Phone number'}
							error={!!errors?.phone}
							errorLabel={errors?.phone?.message}
							type="tel"
							{...field}
							onChange={(value) => {
								setPhoneValue(value);
								setValue('phone', value, { shouldValidate: true });
							}}
							value={phoneValue}
						/>
					)}
				/>
				<TextInput
					type="email"
					label={'Email (optional)'}
					error={!!errors?.email}
					errorLabel={errors?.email?.message}
					{...register('email')}
				/>
			</SpacedColumn>
			<SpacedColumn marginBottom="xxxl" gap="lg">
				<FullWidthSubmitButton label="Save" variant="secondary" submitting={submitting} />
				{isExistingContact && (
					<FullWidthButton label="Delete" labelSize="sm" onClick={onDelete} type="button" />
				)}
				{showCancel && (
					<AlignedLink alignSelf="center" variant="underline" to="..">
						Cancel
					</AlignedLink>
				)}
			</SpacedColumn>
		</FullWidthForm>
	);
};

export default EmergencyContactsEditStep;
